import type { VeroScene as SceneName } from "@handvero/shared";
import { PictoCard } from "./Pictograms";

/*
 * Vero im Schulungsfilm: kleine, gezeichnete Szenen, die in der App selbst ablaufen (keine Videodatei, kein fremder Dienst).
 * Jede Szene gibt Vero eine Haltung zur Folie: winken, zeigen, alarmieren, gehen, nachdenken, abhaken.
 */

function VeroBody({ scene }: { scene: SceneName }) {
  return (
    <svg className={`vs-vero vs-${scene}`} viewBox="0 0 96 112" aria-hidden="true">
      <ellipse className="vs-shadow" cx="48" cy="106" rx="24" ry="4" />
      <g className="vs-legs">
        <rect className="vs-leg l" x="34" y="84" width="9" height="20" rx="4.500" />
        <rect className="vs-leg r" x="53" y="84" width="9" height="20" rx="4.500" />
      </g>
      <g className="vs-all">
        <g className="vs-arm-l">
          <rect x="12" y="58" width="9" height="24" rx="4.500" />
        </g>
        <g className="vs-arm-r">
          <rect x="75" y="58" width="9" height="24" rx="4.500" />
          <circle cx="79.500" cy="84" r="6" />
        </g>
        <g transform="translate(16 12) scale(1.25)">
          <path className="vero-helmet" d="M14 22c0-10 8-16 18-16s18 6 18 16z" />
          <rect className="vero-brim" x="9" y="20" width="46" height="5" rx="2.500" />
          <rect className="vero-body" x="12" y="23" width="40" height="35" rx="14" />
          <rect className="vero-visor" x="18" y="30" width="28" height="17" rx="8.500" />
          <g className="vero-eyes">
            <circle cx="26.500" cy="38.500" r="3" />
            <circle cx="37.500" cy="38.500" r="3" />
          </g>
          <path className="vero-mouth" d="M28.500 51.500q3.500 2.600 7 0" />
        </g>
      </g>
    </svg>
  );
}

/** Zusätze je Szene: Wellen beim Alarm, Denkblasen, Haken. */
function Props({ scene }: { scene: SceneName }) {
  switch (scene) {
    case "alarm":
      return (
        <div className="vs-props" aria-hidden="true">
          <i className="vs-ring" />
          <i className="vs-ring two" />
          <b className="vs-bang">!</b>
        </div>
      );
    case "think":
      return (
        <div className="vs-props" aria-hidden="true">
          <i className="vs-bub b1" />
          <i className="vs-bub b2" />
          <i className="vs-bub b3">?</i>
        </div>
      );
    case "check":
      return (
        <div className="vs-props" aria-hidden="true">
          <svg className="vs-tick" viewBox="0 0 48 48">
            <circle cx="24" cy="24" r="22" />
            <path d="M13 25l8 8 14-17" />
          </svg>
        </div>
      );
    case "walk":
      return (
        <div className="vs-props" aria-hidden="true">
          <i className="vs-path" />
        </div>
      );
    case "point":
      return (
        <div className="vs-props" aria-hidden="true">
          <i className="vs-spark s1" />
          <i className="vs-spark s2" />
        </div>
      );
    default:
      return (
        <div className="vs-props" aria-hidden="true">
          <i className="vs-wave" />
        </div>
      );
  }
}

/** Die Bühne rechts im Film: Sicherheitszeichen der Folie und Vero darunter. */
export function VeroStage({ scene = "point", pictos = [] }: { scene?: SceneName; pictos?: string[] }) {
  return (
    <div className={`vs${pictos.length ? " has-pictos" : ""}`} aria-hidden={pictos.length ? undefined : true}>
      {pictos.length > 0 && (
        <div className="vs-pictos" style={{ ["--n" as string]: pictos.length }}>
          {pictos.map((p, i) => (
            <div key={p} className="vs-picto" style={{ animationDelay: `${0.35 + i * 0.18}s` }}>
              <PictoCard id={p} size={pictos.length > 2 ? 92 : 116} />
            </div>
          ))}
        </div>
      )}
      <div className="vs-actor">
        <Props scene={scene} />
        <VeroBody scene={scene} />
      </div>
    </div>
  );
}
